import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import {
  Button,
  Card,
  Icon,
  Spacing,
  StyledDiv,
  Typography,
} from '@taleemabad/dsm/components';

export interface SchoolData {
  uuid: string;
  name: string;
  emis: number;
  address: string;
  city: string;
}

export interface SchoolListProps {
  schools: SchoolData[];
  onAddSchool: () => void;
  onSchoolClick: (uuid: string) => void;
  onDeleteSchool: (uuid: string) => void;
}

export const SchoolList = ({
  schools,
  onAddSchool,
  onSchoolClick,
  onDeleteSchool,
}: SchoolListProps) => {
  const handleDeleteClick = (e: React.MouseEvent, uuid: string) => {
    e.stopPropagation();
    onDeleteSchool(uuid);
  };

  return (
    <StyledDiv flexDirection="column" width="100%">
      <Spacing marginTop="xl" marginBottom="xl">
        <StyledDiv justifyContent="space-between" alignItem="center">
          <Typography variant="textHeadlineMedium">
            School Management
          </Typography>
          <Button icon="add" onClick={onAddSchool}>
            Add School
          </Button>
        </StyledDiv>
      </Spacing>

      {schools.length > 0 ? (
        <StyledCardsContainer>
          {schools.map((school) => (
            <Card
              variant="outline"
              key={school.uuid}
              onClick={() => onSchoolClick(school.uuid)}
            >
              <StyledHeaderDiv>
                <StyledSchoolInfo>
                  <Typography variant="textTitleMedium">
                    {school.name}
                  </Typography>
                  <Spacing marginTop="xs" />
                  <Typography variant="textBodyMedium">
                    EMIS: {school.emis}
                  </Typography>
                </StyledSchoolInfo>
                <StyledIconButton
                  onClick={(e) => handleDeleteClick(e, school.uuid)}
                  aria-label="Delete School"
                >
                  <Icon name="delete" />
                </StyledIconButton>
              </StyledHeaderDiv>
              <Spacing marginTop="md" />
              <Typography variant="textBodyMedium">{school.address}</Typography>
              <Spacing marginTop="xs" />
              <Typography variant="textBodyMedium">{school.city}</Typography>
            </Card>
          ))}
        </StyledCardsContainer>
      ) : (
        <StyledEmptyState>
          <Icon name="school" />
          <Spacing marginTop="lg" />
          <Typography variant="textTitleMedium">No Schools Added</Typography>
          <Spacing marginTop="sm" />
          <Typography variant="textBodyMedium">
            Start by adding your first school
          </Typography>
        </StyledEmptyState>
      )}
    </StyledDiv>
  );
};

const StyledCardsContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--rk-spacing-lg);

  ${mq(BreakpointSizes.desktop)} {
    grid-template-columns: repeat(3, 1fr);
    gap: var(--rk-spacing-xl);
  }
`;

const StyledIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--rk-spacing-xs);
  color: var(--rk-color-on-surface-secondary);
  transition: color 0.2s;

  &:hover {
    color: var(--rk-color-on-surface-primary);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const StyledHeaderDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const StyledSchoolInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--rk-spacing-xxxxxxl) 0;
  text-align: center;
  min-height: 40vh;
`;
